import { Checkbox, Dots, TooltipV2 } from '@cimplico/components';
import { ArrowRightIcon, CalculatorIcon, EllipsisHorizontalIcon, LinkIcon } from '@heroicons/react/20/solid';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CWTRef, CWTSchemaField } from '../../../../../types/custom-worksheet-v2';
import {
    selectCurrentFocusProperty,
    selectRenamingProperty,
    selectUpdatingReferenceField,
    selectUpdatingTypeField,
} from '../../../store/selectors';
import { getReference } from '../../../../../components/custom-worksheet/utils';
import { editField, removeProperty, setCurrentFocusProperty } from '../../../store/slice';
import { ReactComponent as TackIcon } from '../../../../../assets/tack.svg';
import { useSearchParams } from 'react-router-dom';
import { EyeSlashIcon, TrashIcon } from '@heroicons/react/24/outline';
import PropertyName from '../PropertyName';
import PropertyReference from '../PropertyReference';
import FieldType from './FieldType';

type FieldProps = {
    field: CWTSchemaField;
    disablePin?: boolean;
};

function Field({ field, disablePin = false }: FieldProps): JSX.Element {
    const dispatch = useDispatch();
    const currentRenamingField = useSelector(selectRenamingProperty);
    const currentUpdatingReferenceField = useSelector(selectUpdatingReferenceField);
    const currentUpdatingTypeField = useSelector(selectUpdatingTypeField);
    const currentFocusProperty = useSelector(selectCurrentFocusProperty);

    const outerDivRef = useRef<HTMLDivElement | null>(null);
    const nameDivRef = useRef<HTMLDivElement | null>(null);
    const referenceDivRef = useRef<HTMLDivElement | null>(null);
    const calculationDivRef = useRef<HTMLDivElement | null>(null);
    const typeDivRef = useRef<HTMLDivElement | null>(null);
    const pinDivRef = useRef<HTMLDivElement | null>(null);
    const trashDivRef = useRef<HTMLDivElement | null>(null);

    const isRenaming = currentRenamingField === field.id;
    const isUpdatingReference = currentUpdatingReferenceField === field.id;
    const isUpdatingType = currentUpdatingTypeField === field.id;
    const isFocused = currentFocusProperty === field.id;

    const [, setParams] = useSearchParams();

    const handleClickOutside = useCallback(
        function handleClickOutside(event: MouseEvent): void {
            if (!(event.target instanceof Element)) {
                return;
            }

            if (outerDivRef?.current && (outerDivRef.current.contains(event.target) || outerDivRef.current.isEqualNode(event.target))) {
                // Don't trigger open side panel if click event happens when field name/type/reference is in editing state
                if (isRenaming || isUpdatingReference || isUpdatingType) {
                    return;
                }

                const withinNameDiv =
                    nameDivRef?.current && (nameDivRef.current.contains(event.target) || nameDivRef.current.isEqualNode(event.target));
                const withinReferenceDiv =
                    referenceDivRef?.current &&
                    (referenceDivRef.current.contains(event.target) || referenceDivRef.current.isEqualNode(event.target));
                const withinCalculationDiv =
                    calculationDivRef?.current &&
                    (calculationDivRef.current.contains(event.target) || calculationDivRef.current.isEqualNode(event.target));
                const withinTypeDiv =
                    typeDivRef?.current && (typeDivRef.current.contains(event.target) || typeDivRef.current.isEqualNode(event.target));
                const withinPinDiv =
                    pinDivRef?.current && (pinDivRef.current.contains(event.target) || pinDivRef.current.isEqualNode(event.target));
                const withinTrashDiv =
                    trashDivRef?.current && (trashDivRef.current.contains(event.target) || trashDivRef.current.isEqualNode(event.target));

                // Don't trigger open side panel if click event is within field name/type/reference area
                if (withinNameDiv || withinReferenceDiv || withinTypeDiv || withinPinDiv || withinTrashDiv || withinCalculationDiv) {
                    return;
                }

                if (currentFocusProperty === field.id) {
                    dispatch(setCurrentFocusProperty(undefined));
                } else {
                    dispatch(setCurrentFocusProperty(field.id));
                }
            }
        },
        [currentFocusProperty, dispatch, field.id, isUpdatingType, isRenaming, isUpdatingReference]
    );

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return (): void => document.removeEventListener('mousedown', handleClickOutside);
    }, [handleClickOutside]);

    function changeTab(): void {
        setParams({ tab: 'calculations', reference: getReference(field.id) });
    }

    function formatTooltipMessage(dependentFields: CWTRef[]): string {
        const fieldReferences: string[] = [];

        for (const dependentField of dependentFields) {
            fieldReferences.push(getReference(dependentField.ref));
        }

        return `${field.name} has dependent fields with these references - ${fieldReferences.join(', ')}.`;
    }

    return (
        <div ref={outerDivRef} className={`${isFocused ? 'bg-indigo-50' : ''} cursor-pointer px-2 flex gap-4 items-center w-full relative`}>
            {isFocused && <div className="absolute top-0 left-0 w-full h-full border border-indigo-600 rounded-md" />}
            <div className="flex gap-0.5">
                <Dots className="text-gray-500 w-4 h-4 m-1" />
                <Checkbox />
            </div>

            {/* NAME */}
            <div className="w-[200px]">
                <PropertyName id={field.id} fieldName={field.name} ref={nameDivRef} />
            </div>

            {/* REFERENCE */}
            <div className="w-[200px]">
                <PropertyReference id={field.id} ref={referenceDivRef} />
            </div>

            {/* TYPE */}
            <div className="w-[200px] p-1 flex gap-4 items-center text-gray-500">
                {field.calculation !== null && (
                    <div
                        ref={calculationDivRef}
                        className="flex pl-2 gap-4 items-center hover:underline hover:cursor-pointer"
                        onClick={changeTab}
                    >
                        <div className="flex gap-1 items-center">
                            <CalculatorIcon className="w-3.5 h-3.5" />
                            <p className="text-sm text-gray-700">Calculation</p>
                        </div>
                        <ArrowRightIcon className="w-3.5 h-3.5" />
                    </div>
                )}
                <FieldType id={field.id} fieldType={field.type} ref={typeDivRef} />
            </div>

            <div className="grow flex gap-2.5 items-center justify-end">
                {field.hidden && (
                    <TooltipV2 message="This field is hidden">
                        <EyeSlashIcon className="text-gray-600 h-4 w-4" />
                    </TooltipV2>
                )}
                {field.quickActions && field.quickActions.length > 0 && (
                    <TooltipV2 message="This field can create reconciling entries">
                        <LinkIcon className="text-gray-600 h-4 w-4" />
                    </TooltipV2>
                )}
                <TooltipV2 message="Field cannot be pinned within a section with a summary figure" disabled={!disablePin}>
                    <div
                        className={`p-1 rounded-[5px] ${disablePin ? 'cursor-default' : 'cursor-pointer hover:bg-gray-200'}`}
                        ref={pinDivRef}
                        onClick={(): void => {
                            if (!disablePin) {
                                dispatch(editField({ id: field.id, details: { pinned: !field.pinned } }));
                            }
                        }}
                    >
                        <TackIcon style={{ stroke: field.pinned ? '' : '#7A7A83', fill: field.pinned ? '#16A249' : undefined }} />
                    </div>
                </TooltipV2>
                <EllipsisHorizontalIcon className="text-gray-500 hover:cursor-pointer m-1 w-4 h-4" />
                <TooltipV2 message={formatTooltipMessage(field.dependentFields || [])} disabled={field.dependentFields.length === 0}>
                    <div
                        className={`p-1 ${
                            field.dependentFields.length === 0 ? 'cursor-pointer rounded-[5px] hover:bg-gray-200' : 'cursor-default'
                        }`}
                        ref={trashDivRef}
                        onClick={
                            field.dependentFields.length === 0
                                ? (): void => {
                                      dispatch(removeProperty(field.id));
                                  }
                                : undefined
                        }
                    >
                        <TrashIcon className="text-gray-500 w-4 h-4" />
                    </div>
                </TooltipV2>
            </div>
        </div>
    );
}

export default Field;
